import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    Tooltip,
    Chip,
    Alert,
    CircularProgress,
    alpha,
    Box,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import BadgeIcon from '@mui/icons-material/Badge';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { T } from '../theme/tokens';
import { SynButton } from './ds';
import { api, apiFetch } from '../api/client';
import { AuthIdentity, notifyIdentityChanged, ROLE_LABELS } from '../auth/identity';
import { describeStaffLoginFailure, StaffLoginFailure } from '../auth/staffLoginError';

/**
 * Staff sign-in/out control (Task #162).
 *
 * Reuses the existing email + one-time invitation-code flow
 * (`POST /site-users/verify-login`) that already mints a role-aware session for
 * ANY SiteUser — including internal staff. On success the backend sets the
 * HttpOnly `spartis_site_session` cookie; the nav then narrows to the staff
 * member's role (see `auth/identity.ts`). This is SEPARATE from the break-glass
 * admin code (AdminAccessControl) and from the public research-preview gate.
 */
export default function StaffAccessControl({
    identity,
    onChanged,
}: {
    identity: AuthIdentity | null;
    onChanged: () => void;
}) {
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<StaffLoginFailure | null>(null);
    const [agreeConfidentiality, setAgreeConfidentiality] = useState(false);

    const signedInStaff = !!(identity && identity.is_staff);
    const roleLabel = identity ? (ROLE_LABELS[identity.role] || identity.role) : '';

    const handleOpen = () => {
        setError(null);
        setCode('');
        setEmail(identity?.user_id ? '' : '');
        setOpen(true);
    };

    const submit = async (e?: React.FormEvent) => {
        e?.preventDefault();
        setError(null);
        if (!email.trim()) {
            setError({
                kind: 'other',
                message: 'Enter the email address you were invited with.',
                hint: 'Staff accounts are created by invitation — use the email your invitation was sent to.',
            });
            return;
        }
        // Redeeming an invitation code = first activation: the confidentiality
        // agreement is required (also enforced server-side).
        if (code.trim() && !agreeConfidentiality) {
            setError({
                kind: 'other',
                message: 'Please agree to the confidentiality notice.',
                hint: 'Check the box confirming your invitation code and sign-in details are for you only.',
            });
            return;
        }
        setSubmitting(true);
        try {
            // Migrated to the shared API client (Synterra A3): 'public' bootstrap
            // context mints the staff session cookie; typed error handling below.
            await api.post(
                '/site-users/verify-login',
                {
                    email: email.trim(),
                    invitation_code: code.trim() || undefined,
                    accept_confidentiality: code.trim() ? agreeConfidentiality : undefined,
                },
                { context: 'public' },
            );
            notifyIdentityChanged();
            onChanged();
            setOpen(false);
        } catch (err) {
            // Classified failure copy: what went wrong + a concrete recovery
            // path (no account vs wrong code vs expired vs rate-limited).
            setError(describeStaffLoginFailure(err));
        } finally {
            setSubmitting(false);
        }
    };

    const signOut = async () => {
        setSubmitting(true);
        try {
            await apiFetch('/site-users/logout', { method: 'POST', context: 'public' });
        } catch { /* ignore */ }
        // If this device was unlocked THROUGH staff sign-in on the AccessGate
        // (code-less `staff: true` grant), signing out must re-lock the gate —
        // there is no preview code backing the unlock. Preview-code grants are
        // left alone.
        try {
            const raw = localStorage.getItem('spartis_access_v2');
            if (raw && JSON.parse(raw).staff) localStorage.removeItem('spartis_access_v2');
        } catch { /* ignore */ }
        notifyIdentityChanged();
        onChanged();
        setSubmitting(false);
        setOpen(false);
    };

    return (
        <>
            <Tooltip title={signedInStaff ? `Signed in as ${roleLabel} — manage staff session` : 'Sign in as staff (role-based access)'}>
                <Chip
                    icon={signedInStaff
                        ? <BadgeIcon sx={{ fontSize: 14 }} />
                        : <PersonOutlineIcon sx={{ fontSize: 14 }} />}
                    label={signedInStaff ? roleLabel : 'Staff sign-in'}
                    size="small"
                    onClick={handleOpen}
                    aria-label="staff access"
                    sx={{
                        height: 22,
                        fontSize: '0.63rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        bgcolor: signedInStaff
                            ? alpha(T.color.teal[700], 0.07)
                            : alpha(T.color.text.muted, 0.08),
                        color: signedInStaff ? T.color.teal[600] : T.color.text.muted,
                        border: `1px solid ${signedInStaff
                            ? alpha(T.color.teal[700], 0.12)
                            : alpha(T.color.text.muted, 0.18)}`,
                        '& .MuiChip-icon': { color: 'inherit' },
                    }}
                />
            </Tooltip>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {signedInStaff ? 'Staff session' : 'Staff sign-in'}
                </DialogTitle>
                <DialogContent>
                    {signedInStaff ? (
                        <Typography sx={{ fontSize: '0.85rem', color: T.color.text.muted, mb: 1, lineHeight: 1.6 }}>
                            Signed in as <strong>{roleLabel}</strong>
                            {identity?.is_read_only ? ' (view-only)' : ''}. Your navigation is
                            scoped to your role and initiative assignments. Sign out to return to
                            the research-preview shell.
                        </Typography>
                    ) : (
                        <>
                            <Typography sx={{ fontSize: '0.85rem', color: T.color.text.muted, mb: 2, lineHeight: 1.6 }}>
                                Sign in with the email Synterra Health invited you with to get role-based
                                access to staff tools. This is separate from the research-preview
                                code and the break-glass admin code.
                            </Typography>
                            <TextField
                                autoFocus
                                fullWidth
                                size="small"
                                type="email"
                                label="Email address"
                                value={email}
                                autoComplete="off"
                                onChange={e => setEmail(e.target.value)}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                size="small"
                                label="Invitation code (first sign-in only)"
                                value={code}
                                autoComplete="off"
                                onChange={e => setCode(e.target.value.toUpperCase())}
                                onKeyDown={e => { if (e.key === 'Enter' && email.trim()) submit(); }}
                                helperText="Only required the first time you sign in."
                                sx={{ '& .MuiOutlinedInput-root': { fontFamily: 'monospace' } }}
                            />
                            {code.trim().length > 0 && (
                                <Box sx={{ mt: 2, p: 1.5, bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px' }}>
                                    <FormControlLabel
                                        sx={{ alignItems: 'flex-start', m: 0 }}
                                        control={(
                                            <Checkbox
                                                size="small"
                                                checked={agreeConfidentiality}
                                                onChange={e => setAgreeConfidentiality(e.target.checked)}
                                                disabled={submitting}
                                                sx={{ mt: -0.5 }}
                                                inputProps={{ 'aria-label': 'Agree to the confidentiality notice' } as React.InputHTMLAttributes<HTMLInputElement>}
                                            />
                                        )}
                                        label={(
                                            <Typography sx={{ fontSize: '0.76rem', color: '#78350f', lineHeight: 1.55 }}>
                                                I understand this invitation and my login details are for me only,
                                                and I agree not to copy, forward, share, or screenshot my
                                                invitation code, sign-in details, or platform content.
                                            </Typography>
                                        )}
                                    />
                                </Box>
                            )}
                        </>
                    )}
                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            <Typography sx={{ fontSize: '0.83rem', fontWeight: 600 }}>
                                {error.message}
                            </Typography>
                            <Typography sx={{ fontSize: '0.78rem', mt: 0.5, lineHeight: 1.5 }}>
                                {error.hint}
                            </Typography>
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    {signedInStaff ? (
                        <>
                            <SynButton variant="ghost" onClick={() => setOpen(false)}>Close</SynButton>
                            <SynButton variant="primary" onClick={signOut} disabled={submitting}>
                                {submitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Sign out'}
                            </SynButton>
                        </>
                    ) : (
                        <>
                            <SynButton variant="ghost" onClick={() => setOpen(false)}>Cancel</SynButton>
                            <SynButton variant="primary" onClick={() => submit()} disabled={submitting || !email.trim()}>
                                {submitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Sign in'}
                            </SynButton>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
}
